import { useState } from 'react';
import { Store, Phone, MapPin, Compass, ShoppingBag, SlidersHorizontal, ShieldAlert, Truck } from 'lucide-react';

import type { AdminStore } from '@/client/types/stores';
import { cn } from '@/lib/utils';

import { BrandSection } from './settings/BrandSection';
import { AttributesSection } from './settings/AttributesSection';
import { VisitSection } from './settings/VisitSection';
import { OperationsSection } from './settings/OperationsSection';
import { DangerSection } from './settings/DangerSection';
import { DeliverySection } from './settings/DeliverySection';

const SECTIONS = [
  { id: 'brand', label: 'Brand', icon: Store },
  { id: 'contact', label: 'Contact & social', icon: Phone },
  { id: 'visit', label: 'Visit', icon: MapPin },
  { id: 'discovery', label: 'Discovery', icon: Compass },
  { id: 'shopping', label: 'How to shop', icon: ShoppingBag },
  { id: 'delivery', label: 'Delivery', icon: Truck },
  { id: 'operations', label: 'Operations', icon: SlidersHorizontal },
  { id: 'danger', label: 'Danger zone', icon: ShieldAlert },
] as const;
type SectionId = (typeof SECTIONS)[number]['id'];

export const SettingsTab: React.FC<{ store: AdminStore; onGoToModeration?: () => void }> = ({
  store,
  onGoToModeration,
}) => {
  const [section, setSection] = useState<SectionId>('brand');

  return (
    <div className="grid gap-6 md:grid-cols-[200px_1fr]">
      <nav className="flex gap-1 overflow-x-auto md:flex-col md:overflow-visible">
        {SECTIONS.map((s) => {
          const Icon = s.icon;
          const active = section === s.id;
          return (
            <button
              key={s.id}
              type="button"
              onClick={() => setSection(s.id)}
              className={cn(
                'flex items-center gap-2 whitespace-nowrap rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                active ? 'bg-teal-50 text-teal-700' : 'text-gray-600 hover:bg-gray-100',
              )}
            >
              <Icon className="h-4 w-4 flex-shrink-0" />
              {s.label}
            </button>
          );
        })}
      </nav>

      <div className="min-w-0">
        {section === 'brand' && <BrandSection store={store} />}
        {section === 'contact' && (
          <AttributesSection store={store} groups={['contact', 'social']} title="Contact" description="How buyers reach the store." />
        )}
        {section === 'visit' && <VisitSection store={store} />}
        {section === 'discovery' && (
          <AttributesSection store={store} groups={['taxonomy']} title="Category & tags" description="How the store is categorised and found." />
        )}
        {section === 'shopping' && (
          <AttributesSection store={store} groups={['shopping']} title="How to shop" description="Payment methods, services and amenities." />
        )}
        {section === 'delivery' && <DeliverySection store={store} />}
        {section === 'operations' && <OperationsSection store={store} />}
        {section === 'danger' && <DangerSection store={store} onGoToModeration={onGoToModeration} />}
      </div>
    </div>
  );
};
